import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  ArrowRight,
  BriefcaseBusiness,
  UserRound,
} from 'lucide-react';
import { db } from '../firebase/firebase';
import { logEventSafe } from '../analytics/analytics';
import { PageBackLink, PagePanel, PageShell } from './MarketingPageShell';

const customErrorMessages = {
  'auth/invalid-credential': 'Invalid credentials. Please try again.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Wrong password. Try again.',
  'auth/email-already-in-use': 'Email is already registered.',
  'auth/weak-password': 'Password is too weak.',
  'auth/invalid-email': 'Invalid email address.',
  'auth/missing-password': 'Password is required.',
  'auth/network-request-failed': 'Network error. Check your connection.',
};

const formInputClassName =
  'w-full rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2.5 text-white placeholder:text-gray-500 transition focus:border-blue-400/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30';

const panelCardClassName = 'rounded-xl border border-white/10 bg-white/[0.03] p-3.5';

const getCustomErrorMessage = (code) =>
  customErrorMessages[code] || 'An unexpected error occurred. Please try again.';

const MessageBanner = ({ variant, children, action }) => {
  const styles =
    variant === 'error'
      ? 'border-red-400/30 bg-red-500/10 text-red-100'
      : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100';

  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm ${styles}`}>
      <div>{children}</div>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
};

const AccountTypeCard = ({ active, icon: Icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-xl border p-3.5 text-left transition ${
      active
        ? 'border-blue-400/40 bg-blue-500/10 shadow-lg shadow-blue-950/20'
        : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
    }`}
  >
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950/60 text-blue-200">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <p className="mt-0.5 text-sm leading-5 text-gray-300">{description}</p>
      </div>
    </div>
  </button>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const modeParam = searchParams.get('mode');
  const accountTypeParam = searchParams.get('accountType');
  const auth = getAuth();

  const initialIsSignUp = modeParam === 'signup';
  const initialAccountType = accountTypeParam === 'team' ? 'team' : 'personal';
  const initialSignUpStep = initialIsSignUp ? (initialAccountType === 'team' ? 2 : 1) : 1;

  const [isSignUp, setIsSignUp] = useState(initialIsSignUp);
  const [signUpStep, setSignUpStep] = useState(initialSignUpStep);
  const [accountType, setAccountType] = useState(initialAccountType);
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hasAgreed, setHasAgreed] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [canResend, setCanResend] = useState(true);

  useEffect(() => {
    logEventSafe('page_view', {
      page_path: location.pathname,
      page_title: isSignUp ? 'Create Account' : 'Login',
    });
  }, [location.pathname, isSignUp]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      if (!user.emailVerified) {
        setError(
          'Please verify your email before signing in. Check your inbox for a verification email.'
        );
        await auth.signOut();
        return;
      }

      logEventSafe('login', {
        method: 'email_password',
        page_path: location.pathname,
      });

      const uSnap = await getDoc(doc(db, 'users', user.uid));
      const userOrg = uSnap.data()?.orgId;
      if (userOrg) {
        const oSnap = await getDoc(doc(db, 'organizations', userOrg));
        if (!oSnap.exists()) {
          await updateDoc(doc(db, 'users', user.uid), { orgId: '', role: 'user' });
        }
      }

      if (redirectParam) {
        if (redirectParam.startsWith('http')) {
          window.location.href = redirectParam;
        } else {
          navigate(redirectParam);
        }
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(getCustomErrorMessage(err.code));
    }
  };

  const handleResendVerification = async () => {
    if (!canResend) return;
    setError('');
    setInfoMessage('');
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(user);
      setInfoMessage('Verification email resent! Please check your inbox.');
      await auth.signOut();
      setCanResend(false);
      setTimeout(() => setCanResend(true), 60000);
    } catch (err) {
      setError(getCustomErrorMessage(err.code));
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');

    if (signUpStep === 1) {
      return;
    }
    if (accountType === 'team' && !companyName.trim()) {
      setError('Please enter your company name.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!hasAgreed) {
      setError(
        'You must agree to the Terms of Service and Privacy Policy before creating an account.'
      );
      return;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{6,100}$/;
    if (!passwordRegex.test(password)) {
      setError(
        'Password must be at least 6 characters long and include an uppercase letter, a lowercase letter, a digit, and a special character.'
      );
      return;
    }

    try {
      const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);

      const profileName =
        accountType === 'team' ? companyName.trim() : displayName.trim();
      if (profileName) {
        await updateProfile(newUser, { displayName: profileName });
      }

      logEventSafe('sign_up', {
        method: 'email_password',
        account_type: accountType,
        page_path: location.pathname,
      });

      await sendEmailVerification(newUser);

      let inviteKey = '';
      if (accountType === 'team') {
        const orgRef = doc(collection(db, 'organizations'));
        inviteKey = orgRef.id;
        await setDoc(orgRef, {
          name: companyName.trim(),
          createdBy: newUser.uid,
          inviteKey,
          memberLimit: 50,
          memberCount: 1,
          createdAt: serverTimestamp(),
        });
        await setDoc(doc(db, 'organizations', inviteKey, 'members', newUser.uid), {
          uid: newUser.uid,
          email: newUser.email,
          role: 'manager',
          joinedAt: serverTimestamp(),
        });
      }

      await setDoc(doc(db, 'users', newUser.uid), {
        uid: newUser.uid,
        email: newUser.email,
        displayName: profileName,
        createdAt: serverTimestamp(),
        role: accountType === 'team' ? 'manager' : 'user',
        ...(accountType === 'team' ? { orgId: inviteKey } : {}),
      });

      setInfoMessage(
        'Account created! A verification email has been sent. Please verify before signing in.'
      );
      await auth.signOut();
      setSignUpStep(1);
      setIsSignUp(false);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(getCustomErrorMessage(err.code));
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    if (!email) {
      setError('Please enter your email first.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setInfoMessage('Password reset email sent! Please check your inbox.');
      logEventSafe('password_reset', {
        page_path: location.pathname,
      });
    } catch (err) {
      setError(getCustomErrorMessage(err.code));
    }
  };

  const toggleMode = () => {
    setIsSignUp((current) => !current);
    setSignUpStep(1);
    setError('');
    setInfoMessage('');
  };

  return (
    <PageShell>
      <div className="space-y-4 py-3">
        <PageBackLink to="/" />

        <div className="mx-auto max-w-xl">
          <PagePanel className="space-y-4 p-4 sm:p-5">
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-[0.22em] text-blue-200">
                {isSignUp ? `Step ${signUpStep} of 2` : 'Sign in'}
              </div>
              <h1 className="text-xl font-semibold text-white sm:text-2xl">
                {isSignUp ? 'Create Account' : 'Welcome back'}
              </h1>
              <p className="text-sm text-gray-400">
                {isSignUp
                  ? signUpStep === 1
                    ? 'Pick the account type you need.'
                    : 'Complete the fields below to finish setup.'
                  : 'Enter your email and password to continue.'}
              </p>
            </div>

            <div className="space-y-3">
              {error ? (
                <MessageBanner
                  variant="error"
                  action={
                    error.startsWith('Please verify') ? (
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={!canResend}
                        className="text-sm font-medium text-blue-200 transition hover:text-white disabled:cursor-not-allowed disabled:text-gray-500"
                      >
                        {canResend ? 'Resend Verification Email' : 'Please wait...'}
                      </button>
                    ) : null
                  }
                >
                  {error}
                </MessageBanner>
              ) : null}

              {infoMessage ? <MessageBanner variant="success">{infoMessage}</MessageBanner> : null}
            </div>

            {isSignUp ? (
              <div className={panelCardClassName}>
                <p className="text-sm text-gray-300">
                  {accountType === 'team'
                    ? 'Team accounts create an organization and invite key during setup.'
                    : 'Personal accounts create a standard user profile with your display name.'}
                </p>
              </div>
            ) : null}

            <form
              onSubmit={isSignUp ? handleSignUp : handleSignIn}
              className="space-y-4"
            >
                  {isSignUp && signUpStep === 1 ? (
                    <div className="space-y-3">
                      <AccountTypeCard
                        active={accountType === 'personal'}
                        icon={UserRound}
                        title="Personal Use"
                        description="For individual workflows, notes, utilities, and custom layouts."
                        onClick={() => setAccountType('personal')}
                      />
                      <AccountTypeCard
                        active={accountType === 'team'}
                        icon={BriefcaseBusiness}
                        title="Business & Team Use"
                        description="For organizations that need invite-based team management and shared billing."
                        onClick={() => setAccountType('team')}
                      />

                      <button
                        type="button"
                        onClick={() => setSignUpStep(2)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-500"
                      >
                        Continue
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      {isSignUp && accountType === 'team' ? (
                        <div>
                          <label
                            htmlFor="companyName"
                            className="mb-2 block text-sm font-medium text-gray-200"
                          >
                            Company Name
                          </label>
                          <input
                            type="text"
                            id="companyName"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            className={formInputClassName}
                            placeholder="Your Company Name"
                            required
                          />
                        </div>
                      ) : null}

                      {isSignUp && accountType === 'personal' ? (
                        <div>
                          <label
                            htmlFor="displayName"
                            className="mb-2 block text-sm font-medium text-gray-200"
                          >
                            Display Name
                          </label>
                          <input
                            type="text"
                            id="displayName"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            className={formInputClassName}
                            placeholder="Enter your display name"
                            required
                          />
                        </div>
                      ) : null}

                      <div>
                        <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-200">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={formInputClassName}
                          placeholder="Enter your email"
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="password"
                          className="mb-2 block text-sm font-medium text-gray-200"
                        >
                          Password
                        </label>
                        <input
                          type="password"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={formInputClassName}
                          placeholder="Enter your password"
                          required
                        />
                      </div>

                      {isSignUp ? (
                        <>
                          <div>
                            <label
                              htmlFor="confirmPassword"
                              className="mb-2 block text-sm font-medium text-gray-200"
                            >
                              Confirm Password
                            </label>
                            <input
                              type="password"
                              id="confirmPassword"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className={formInputClassName}
                              placeholder="Re-enter your password"
                              required
                            />
                          </div>

                          <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                            <input
                              type="checkbox"
                              id="agree"
                              checked={hasAgreed}
                              onChange={(e) => setHasAgreed(e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-white/20 bg-slate-950/60 text-blue-600 focus:ring-blue-500/50"
                              required
                            />
                            <span className="text-sm leading-6 text-gray-300">
                              I agree to the{' '}
                              <Link to="/policies" className="text-blue-300 transition hover:text-white">
                                Terms of Service
                              </Link>{' '}
                              and{' '}
                              <Link to="/policies" className="text-blue-300 transition hover:text-white">
                                Privacy Policy
                              </Link>
                              .
                            </span>
                          </label>
                        </>
                      ) : (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleResetPassword}
                            className="text-sm font-medium text-blue-300 transition hover:text-white"
                          >
                            Forgot password?
                          </button>
                        </div>
                      )}

                      <button
                        type="submit"
                        className="w-full rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-500"
                      >
                        {isSignUp ? 'Create Account' : 'Sign In'}
                      </button>

                      {isSignUp ? (
                        <button
                          type="button"
                          onClick={() => setSignUpStep(1)}
                          className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 font-medium text-white transition hover:bg-white/[0.08]"
                        >
                          Back
                        </button>
                      ) : null}
                    </>
                  )}

                  <div className="border-t border-white/10 pt-4 text-center text-sm text-gray-400">
                    {isSignUp ? 'Already have an account?' : 'Need an account?'}{' '}
                    <button
                      type="button"
                      onClick={toggleMode}
                      className="font-medium text-blue-300 transition hover:text-white"
                    >
                      {isSignUp ? 'Sign In' : 'Create Account'}
                    </button>
                  </div>
            </form>
          </PagePanel>
        </div>
      </div>
    </PageShell>
  );
};

export default LoginPage;
